/**
 * `<Menu>` / `<MenuItem>` — shared contextual-menu primitive.
 *
 * Implements the Google Material 3 menu pattern with full WAI-ARIA
 * keyboard parity:
 *
 *   - `role="menu"` on the container, `role="menuitem"` on each item.
 *   - **Roving tabindex**: only the active item carries `tabIndex={0}`;
 *     every other item is `tabIndex={-1}`. The container itself is
 *     never focusable.
 *   - **Arrow keys**: `ArrowDown` / `ArrowUp` move the active index,
 *     skipping disabled items, wrapping at the ends.
 *   - **Home / End**: jump to first / last enabled item.
 *   - **Enter / Space**: activate the focused item (consumed and
 *     `preventDefault()`-ed so a parent button does not also fire).
 *   - **Escape**: closes the menu via the `onClose` callback.
 *   - **Type-ahead**: typing a printable character (`a–z`, `0–9`,
 *     symbols) jumps to the next item whose label starts with the
 *     typed prefix (case-insensitive). The prefix buffer resets after
 *     500 ms of inactivity, matching Google Material 3 / Cloud
 *     Console.
 *
 * The component is uncontrolled-by-default: it owns the focused index
 * internally and auto-focuses the first enabled item on mount so a
 * keyboard user can immediately use ArrowDown / typing without a
 * second Tab press.
 *
 * Source-of-truth citations:
 *   - `DESIGN_POLICY.md` §5 ("keyboard reachability", "visible focus
 *     ring", "40 px touch target").
 *   - `ICON_SYSTEM_PLAYBOOK.md` (rounded Material Symbols only).
 *   - `GOOGLE_DESIGN_PATTERN_ANALYSIS.md` (menu / contextual-menu
 *     pattern parity with Cloud Console).
 *   - WP-2 supervisor verdict §2.2 Note 3 — defers the roving
 *     tabindex / type-ahead implementation to WP-11.
 *   - WAI-ARIA Authoring Practices Guide — Menu (button) pattern.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { tokens } from '../tokens';
import { Icon, type IconName } from './Icon';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MenuItemDef {
  /**
   * Stable id used by the action handler. Consumers with a tighter
   * union should narrow at the call site (`onSelect={(id) =>
   * doThing(id as MyActionId)}`); the primitive itself stays
   * generic-free to compose cleanly with React's standard `JSX.Element`
   * boundaries.
   */
  readonly id: string;
  /** Visible label and the type-ahead match key. */
  readonly label: string;
  /** Optional leading icon (Material Symbols rounded). */
  readonly icon?: IconName;
  /** Trailing keyboard hint (e.g. "Ctrl K"). Rendered in the mono role. */
  readonly shortcut?: string;
  /** When true, the item is greyed and not selectable. */
  readonly disabled?: boolean;
  /**
   * Optional reason surfaced via `title` + a `data-disabled-reason`
   * data attribute when disabled, so screen readers (via custom
   * `aria-describedby` wiring) and hover tooltips can explain the
   * gate.
   */
  readonly disabledReason?: string;
}

export interface MenuProps {
  /** Items to render, in display order. */
  readonly items: readonly MenuItemDef[];
  /** Required accessible name on the container. */
  readonly 'aria-label': string;
  /** Action handler called when the user activates an item. */
  readonly onSelect: (id: string) => void;
  /** Optional close handler — called when the user presses Escape. */
  readonly onClose?: () => void;
  /**
   * SSR-test seam: pass an explicit initial focus index. Production
   * callers should leave this undefined; the menu auto-focuses the
   * first enabled item. Use `-1` to start with no item focused.
   */
  readonly defaultFocusIndex?: number;
  /** Optional outer style override; applied after the tokenized base. */
  readonly style?: CSSProperties;
  /** Test id consumed by Playwright / RTL specs. */
  readonly 'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TYPE_AHEAD_RESET_MS = 500;

function nextEnabled(
  items: readonly MenuItemDef[],
  from: number,
  delta: 1 | -1,
): number {
  if (items.length === 0) return -1;
  const total = items.length;
  for (let step = 1; step <= total; step += 1) {
    const idx = (from + delta * step + total) % total;
    if (!items[idx].disabled) return idx;
  }
  return -1;
}

function firstEnabled(items: readonly MenuItemDef[]): number {
  return items.findIndex((item) => !item.disabled);
}

function lastEnabled(items: readonly MenuItemDef[]): number {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (!items[i].disabled) return i;
  }
  return -1;
}

function findByPrefix(
  items: readonly MenuItemDef[],
  prefix: string,
  from: number,
): number {
  if (!prefix) return from;
  const lower = prefix.toLowerCase();
  const total = items.length;
  for (let step = 1; step <= total; step += 1) {
    const idx = (from + step) % total;
    const item = items[idx];
    if (!item.disabled && item.label.toLowerCase().startsWith(lower)) {
      return idx;
    }
  }
  return from;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Menu({
  items,
  'aria-label': ariaLabel,
  onSelect,
  onClose,
  defaultFocusIndex,
  style,
  'data-testid': testId,
}: MenuProps) {
  const initialIndex = useMemo(() => {
    if (typeof defaultFocusIndex === 'number') return defaultFocusIndex;
    return firstEnabled(items);
  }, [defaultFocusIndex, items]);
  const [focusIndex, setFocusIndex] = useState<number>(initialIndex);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const typeAheadRef = useRef<{ buffer: string; timer: number | null }>({
    buffer: '',
    timer: null,
  });
  const idBase = useId();

  // Keep refs aligned with the items length.
  if (itemRefs.current.length !== items.length) {
    itemRefs.current = items.map(
      (_, i) => itemRefs.current[i] ?? null,
    );
  }

  // Auto-focus the active item when it changes (production callers).
  // Defaulting to the first enabled item gives keyboard users immediate
  // arrow-key + type-ahead access without a second Tab press.
  useEffect(() => {
    if (focusIndex < 0) return;
    const node = itemRefs.current[focusIndex];
    if (node && document.activeElement !== node) {
      node.focus({ preventScroll: true });
    }
  }, [focusIndex]);

  // Re-clamp the focus index if items mutate while the menu is open.
  useEffect(() => {
    if (focusIndex >= items.length) {
      setFocusIndex(firstEnabled(items));
    }
  }, [items, focusIndex]);

  // Clean up the pending type-ahead timer on unmount. Capture the
  // mutable cell so the linter can prove the cleanup reads the same
  // value that was current when the effect ran.
  useEffect(() => {
    const state = typeAheadRef.current;
    return () => {
      if (state.timer !== null) window.clearTimeout(state.timer);
    };
  }, []);

  const handleTypeAhead = useCallback(
    (key: string) => {
      const state = typeAheadRef.current;
      if (state.timer !== null) window.clearTimeout(state.timer);
      state.buffer += key.toLowerCase();
      state.timer = window.setTimeout(() => {
        state.buffer = '';
        state.timer = null;
      }, TYPE_AHEAD_RESET_MS);
      const next = findByPrefix(items, state.buffer, focusIndex);
      if (next !== focusIndex) setFocusIndex(next);
    },
    [focusIndex, items],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = nextEnabled(
          items,
          focusIndex < 0 ? -1 : focusIndex,
          1,
        );
        if (next >= 0) setFocusIndex(next);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const next = nextEnabled(
          items,
          focusIndex < 0 ? items.length : focusIndex,
          -1,
        );
        if (next >= 0) setFocusIndex(next);
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        const next = firstEnabled(items);
        if (next >= 0) setFocusIndex(next);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        const next = lastEnabled(items);
        if (next >= 0) setFocusIndex(next);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        if (focusIndex < 0) return;
        const item = items[focusIndex];
        if (!item || item.disabled) return;
        event.preventDefault();
        onSelect(item.id);
        return;
      }
      // Type-ahead: single printable character (ignore modifiers and
      // navigation keys). Length-1 covers letters/digits/symbols. Skip
      // when Ctrl/Meta/Alt is held so palette-style shortcuts still
      // bubble out of the menu container.
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        handleTypeAhead(event.key);
      }
    },
    [focusIndex, handleTypeAhead, items, onClose, onSelect],
  );

  return (
    <div
      role="menu"
      aria-label={ariaLabel}
      data-testid={testId}
      onKeyDown={handleKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[0],
        ...style,
      }}
    >
      {items.map((item, idx) => {
        const isFocused = idx === focusIndex;
        const itemId = `${idBase}-item-${idx}`;
        return (
          <button
            key={item.id}
            id={itemId}
            type="button"
            role="menuitem"
            ref={(node) => {
              itemRefs.current[idx] = node;
            }}
            tabIndex={isFocused ? 0 : -1}
            disabled={item.disabled}
            aria-disabled={item.disabled || undefined}
            data-disabled-reason={
              item.disabled && item.disabledReason
                ? item.disabledReason
                : undefined
            }
            title={item.disabled ? item.disabledReason : undefined}
            aria-label={item.label}
            onClick={(event) => {
              event.preventDefault();
              if (item.disabled) return;
              onSelect(item.id);
            }}
            onMouseEnter={() => {
              if (!item.disabled) setFocusIndex(idx);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              minHeight: tokens.density.touchMin,
              paddingInline: tokens.space[3],
              border: 0,
              background: isFocused
                ? tokens.color.surface[3]
                : 'transparent',
              color: item.disabled
                ? tokens.color.ink.muted
                : tokens.color.ink.primary,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.sm,
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              borderRadius: tokens.radius.input,
              textAlign: 'left',
            }}
          >
            {item.icon ? <Icon name={item.icon} size={20} decorative /> : null}
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.shortcut ? (
              <span
                aria-hidden="true"
                style={{
                  fontFamily: tokens.font.family.mono,
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.secondary,
                }}
              >
                {item.shortcut}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
